import loadable from "@loadable/component"
import queryString from "query-string"
import { useEffect, useMemo } from "react"
import { Helmet } from "react-helmet-async"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"

import { setExperiment } from "app-store-v2/app/actions"
import useCollection from "areas/collection/hoc/use-collection"
import LandingElement from "areas/landing/landing-element"
import LandingLoading from "areas/landing/loaders"
import IntersectionWrapper from "components/common/intersection-wrapper"
import { openModal } from "components/modal"
import { isTestMode, retry } from "helpers/application"
import { logCurrentPageInCollectionStats } from "helpers/collection"
import useScrollRestoration from "helpers/use-scroll-restoration"
import withLanding from "hoc/landing/with-landing"

const HomepageBannerCarousel = loadable(() =>
  retry(() => import("./homepage-banner-carousel"))
)
const LandingHeroBanner = loadable(() =>
  retry(() => import("areas/landing/elements/hero-banner"))
)
const LandingCollectionMenu = loadable(() =>
  retry(() => import("areas/landing/elements/landing-collection-menu"))
)

const Homepage = () => {
  const { search } = useLocation()
  const { data, isRefetching } = useCollection()
  const dispatch = useDispatch()
  const bannerIds = useSelector((state) => state.sale.homepage_hero_banner_ids)
  const heroBanners = useSelector((state) => state.sale.homepage_hero_banners)
  const announcementBarExp = useSelector(
    (state) => state.experiment.announcementBar
  )
  const homepageQuickNavExp = useSelector(
    (state) => state.experiment.homepageQuickNav
  )

  const quickNavEl = useMemo(() => {
    if (!data || homepageQuickNavExp.variant_id === 0) return null

    const quickNav = (data.elements_list || []).find(
      (el) => el.anchor_name === "top-jewelry-collections"
    )

    if (!quickNav) return null

    return (data.elements || []).find((el) => el.id === quickNav.id)
  }, [data, homepageQuickNavExp])

  const preloadableBannerImages =
    heroBanners && bannerIds?.length > 0 && heroBanners[bannerIds[0]]
      ? (heroBanners[bannerIds[0]].data?.items || [])
          .filter((item) => Object.keys(item.image || {}).length > 0)
          .map((item) => item.image)
      : []

  const handleQuickNavItemClick = (url) => {
    try {
      if (!isTestMode) {
        dataLayer.push({
          clickedUrl: url,
          event: "clicked-homepage-quick-nav",
          variant: homepageQuickNavExp.variant_id,
        })
      }
    } catch (e) {
      console.log("Error sending clicked-homepage-quick-nav event", e)
    }
  }

  useScrollRestoration()

  useEffect(() => {
    if (homepageQuickNavExp) {
      dispatch(
        setExperiment({
          homepageQuickNav: homepageQuickNavExp,
        })
      )
    }
  }, [homepageQuickNavExp, dispatch])

  useEffect(() => {
    logCurrentPageInCollectionStats("homepage")

    if (!isTestMode) {
      submit_ga_event_click("Page", "Viewed Homepage")
      dataLayer.push({
        event: "viewed-homepage",
      })
    }
  }, [])

  useEffect(() => {
    if (announcementBarExp) {
      dispatch(setExperiment({ announcementBar: announcementBarExp }))
    }
  }, [announcementBarExp])

  useEffect(() => {
    if (typeof window !== "undefined") {
      const signin = queryString.parse(search).signin === "true"

      if (signin) {
        const user_obj = window.localStorage.getItem("user_obj")
        const loggedIn =
          user_obj && Object.keys(JSON.parse(user_obj)).length > 0

        if (!loggedIn) {
          openModal("#authenticate")
        }
      }
    }
  }, [])

  return (
    <>
      <Helmet encodeSpecialCharacters={false}>
        {/* eslint-disable sort-keys, react/jsx-sort-props, react/no-unknown-property */}
        {preloadableBannerImages.map((image, i) =>
          (image.srcSet || []).map((srcset) => (
            <link
              key={`homepage-banner-preload-${image.id || i}-${srcset.media}`}
              rel="preload"
              fetchPriority="high"
              as="image"
              imageSrcSet={srcset.srcSet}
              imageSizes={srcset.sizes}
              media={srcset.media}
            />
          ))
        )}
        {(data.preloadable_images || []).map((image, i) =>
          (image.srcSet || []).map((srcset) => (
            <link
              key={`homepage-image-preload-${image.id || i}-${srcset.media}`}
              rel="preload"
              fetchPriority="high"
              as="image"
              imageSrcSet={srcset.srcSet}
              imageSizes={srcset.sizes}
              media={srcset.media}
            />
          ))
        )}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "http://schema.org",
            "@type": "Organization",
            url: `https://${sl.config.site_url}`,
            name: "Jewlr",
            logo: {
              "@type": "ImageObject",
              url: "https://assets.jewlr.com/images/logos/jewlr-logo.svg",
            },
            email: sl.config.customer_service_email,
            contactPoint: [
              {
                "@type": "ContactPoint",
                email: sl.config.customer_service_email,
                telephone: sl.config.customer_service_toll_free,
                contactType: "customer service",
              },
            ],
            sameAs: [
              "https://www.facebook.com/jewlrcom",
              "https://www.instagram.com/jewlr/",
              "https://www.youtube.com/channel/UC11M7JozPazDfSi4dMf_wOg",
              "https://www.pinterest.com/jewlrcom/_shop/",
              "https://tiktok.com/@jewlr_official",
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "http://schema.org",
            "@type": "WebSite",
            url: `https://${sl.config.site_url}`,
            potentialAction: {
              "@type": "SearchAction",
              target: `https://${sl.config.site_url}/search?k={search_term}`,
              query: "required",
              "query-input": "required name=search_term",
            },
          })}
        </script>
        {/* eslint-enable sort-keys, react/jsx-sort-props, react/no-unknown-property */}
      </Helmet>
      {(homepageQuickNavExp.variant_id === 1 ||
        homepageQuickNavExp.variant_id === 2) &&
        quickNavEl && (
          <LandingCollectionMenu
            element={quickNavEl}
            handleItemClick={handleQuickNavItemClick}
            homepageQuickNavExp={homepageQuickNavExp.variant_id}
            lazyload={false}
          />
        )}
      {bannerIds?.length > 1 ? (
        <HomepageBannerCarousel banners={heroBanners} order={bannerIds} />
      ) : bannerIds?.length == 1 ? (
        <LandingHeroBanner
          element={heroBanners && heroBanners[bannerIds[0]]}
          homepage
          lazyload={false}
        />
      ) : null}
      {(data?.elements_list || [])
        .filter(
          (el) =>
            homepageQuickNavExp.variant_id === 0 || el.id !== quickNavEl?.id
        )
        .map((el, index) => {
          const elId = el.anchor_name || el.id
          const element = data.elements.find((elm) => elm.id === el.id)
          let content

          if (element) {
            // SSR (Pre-render) the first THREE elements
            if (index <= 2) {
              content = (
                <LandingElement
                  el={element}
                  index={index}
                  wrapTheChild={false}
                />
              )
            } else {
              // Lazy-loaded element
              content = (
                <IntersectionWrapper>
                  <LandingElement
                    el={element}
                    index={index}
                    wrapTheChild={false}
                  />
                </IntersectionWrapper>
              )
            }
          }

          return (
            <div id={elId} key={`homepage-wrapper-${index}`}>
              {content}
            </div>
          )
        })}
      {isRefetching && <LandingLoading partial />}
    </>
  )
}

export default withLanding(Homepage)
